
import React from 'react';

interface Product {
  id: string;
  name: string;
  features: string[];
}

interface ProductFeaturesProps {
  product: Product;
}

/**
 * Renders a list of product features.
 * Includes defensive checks and a fallback UI if the feature list is empty or missing.
 */
export default function ProductFeatures({ product }: ProductFeaturesProps) {
  const featureIcons = [
    'ri-check-double-line',
    'ri-flashlight-line',
    'ri-settings-3-line',
    'ri-bar-chart-box-line',
    'ri-global-line',
    'ri-headphone-line',
  ];

  // Guard against missing or non‑array features to avoid runtime errors.
  const features = Array.isArray(product?.features) ? product.features : [];

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Key Features</h2>
          <p className="text-gray-500 max-w-xl mx-auto">
            Everything you need to succeed, packed into one powerful platform
          </p>
        </div>

        {features.length === 0 ? (
          <p className="text-center text-gray-500">No features available.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-lg mb-4">
                  <i
                    className={`${featureIcons[index % featureIcons.length]} text-teal-600 text-lg`}
                    aria-hidden="true"
                  ></i>
                </div>
                <p className="text-gray-800 font-semibold text-sm leading-relaxed">{feature}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
